/**
 * Subscription tier configuration and feature flags
 *
 * This module defines the ProfitOS pricing tiers and their associated
 * limits and feature flags. Used by:
 * - Payment service for subscription management
 * - API gateway for feature gating
 * - Dashboard for usage display
 */
// =============================================================================
// Tier Configuration
// =============================================================================
export const SUBSCRIPTION_TIERS = {
    starter: {
        name: 'Starter',
        displayPrice: { monthly: 0, annual: 0 },
        revenueLimitCents: 100_000, // $1,000/month
        aiContentLimit: 3,
        affiliateLinksLimit: 25,
        affiliateProgramsLimit: 1,
        teamMembersLimit: 1,
        dataRetentionDays: 30,
        features: {
            linkOptimization: false,
            contentDecayAlerts: false,
            autoRegeneration: false,
            utmTracking: false,
            conversionFunnels: false,
            customBranding: false,
            multiTenant: false,
            apiAccess: false,
            prioritySupport: false,
            dedicatedCsm: false,
        },
    },
    growth: {
        name: 'Growth',
        displayPrice: { monthly: 79, annual: 67 },
        revenueLimitCents: 1_000_000, // $10,000/month
        aiContentLimit: 10,
        affiliateLinksLimit: 100,
        affiliateProgramsLimit: 3,
        teamMembersLimit: 2,
        dataRetentionDays: 90,
        features: {
            linkOptimization: true,
            contentDecayAlerts: true,
            autoRegeneration: false,
            utmTracking: true,
            conversionFunnels: false,
            customBranding: false,
            multiTenant: false,
            apiAccess: false,
            prioritySupport: true,
            dedicatedCsm: false,
        },
    },
    pro: {
        name: 'Pro',
        displayPrice: { monthly: 199, annual: 179 },
        revenueLimitCents: 5_000_000, // $50,000/month
        aiContentLimit: 50,
        affiliateLinksLimit: -1, // unlimited
        affiliateProgramsLimit: -1, // unlimited
        teamMembersLimit: 5,
        dataRetentionDays: 365,
        features: {
            linkOptimization: true,
            contentDecayAlerts: true,
            autoRegeneration: true,
            utmTracking: true,
            conversionFunnels: true,
            customBranding: true,
            multiTenant: false,
            apiAccess: true,
            prioritySupport: true,
            dedicatedCsm: false,
        },
    },
    scale: {
        name: 'Scale',
        displayPrice: { monthly: 499, annual: 449 },
        revenueLimitCents: -1, // unlimited
        aiContentLimit: -1, // unlimited
        affiliateLinksLimit: -1, // unlimited
        affiliateProgramsLimit: -1, // unlimited
        teamMembersLimit: -1, // unlimited
        dataRetentionDays: -1, // unlimited
        features: {
            linkOptimization: true,
            contentDecayAlerts: true,
            autoRegeneration: true,
            utmTracking: true,
            conversionFunnels: true,
            customBranding: true,
            multiTenant: true,
            apiAccess: true,
            prioritySupport: true,
            dedicatedCsm: true,
        },
    },
};
// =============================================================================
// Stripe Price ID Mapping
// =============================================================================
/**
 * Maps Stripe price IDs to subscription tiers.
 * These should be populated from environment variables in production.
 */
export const STRIPE_PRICE_TO_TIER = {
    // Monthly prices
    price_starter_monthly: 'starter',
    price_growth_monthly: 'growth',
    price_pro_monthly: 'pro',
    price_scale_monthly: 'scale',
    // Annual prices
    price_growth_annual: 'growth',
    price_pro_annual: 'pro',
    price_scale_annual: 'scale',
};
/**
 * Maps tiers to their Stripe price IDs.
 * Monthly and annual variants.
 */
export const TIER_TO_STRIPE_PRICE = {
    starter: {
        monthly: 'price_starter_monthly',
        annual: 'price_starter_monthly', // Free tier has no annual
    },
    growth: {
        monthly: 'price_growth_monthly',
        annual: 'price_growth_annual',
    },
    pro: {
        monthly: 'price_pro_monthly',
        annual: 'price_pro_annual',
    },
    scale: {
        monthly: 'price_scale_monthly',
        annual: 'price_scale_annual',
    },
};
// =============================================================================
// Helper Functions
// =============================================================================
/**
 * Get tier configuration by tier name
 */
export function getTierConfig(tier) {
    return SUBSCRIPTION_TIERS[tier];
}
/**
 * Resolve subscription tier from Stripe price ID
 */
export function getTierFromPriceId(priceId) {
    return STRIPE_PRICE_TO_TIER[priceId] ?? 'starter';
}
/**
 * Get Stripe price ID for a tier and billing cycle
 */
export function getStripePriceId(tier, cycle) {
    return TIER_TO_STRIPE_PRICE[tier][cycle];
}
/**
 * Check if a feature is available for a tier
 */
export function canAccessFeature(tier, feature) {
    return SUBSCRIPTION_TIERS[tier].features[feature];
}
/**
 * Get the limit value for a tier
 * Returns -1 for unlimited
 */
export function getLimit(tier, limit) {
    return SUBSCRIPTION_TIERS[tier][limit];
}
/**
 * Check if current usage is within the tier's limit
 * Returns true if within limit or if limit is unlimited (-1)
 */
export function isWithinLimit(tier, limit, currentUsage) {
    const max = getLimit(tier, limit);
    if (max === -1)
        return true; // unlimited
    return currentUsage < max;
}
/**
 * Calculate percentage of limit used
 * Returns 0 for unlimited limits
 */
export function getLimitUsagePercentage(tier, limit, currentUsage) {
    const max = getLimit(tier, limit);
    if (max === -1)
        return 0; // unlimited shows as 0%
    return Math.min((currentUsage / max) * 100, 100);
}
/**
 * Get the minimum tier required for a feature
 */
export function getMinimumTierForFeature(feature) {
    const tiers = ['starter', 'growth', 'pro', 'scale'];
    for (const tier of tiers) {
        if (SUBSCRIPTION_TIERS[tier].features[feature]) {
            return tier;
        }
    }
    return 'scale'; // Default to highest tier if not found
}
/**
 * Get list of features available in a tier but not in the previous tier
 * Useful for upgrade prompts
 */
export function getNewFeaturesInTier(tier) {
    const tierOrder = ['starter', 'growth', 'pro', 'scale'];
    const currentIndex = tierOrder.indexOf(tier);
    if (currentIndex === 0) {
        // Starter tier - return all its features
        return Object.entries(SUBSCRIPTION_TIERS.starter.features)
            .filter(([, enabled]) => enabled)
            .map(([feature]) => feature);
    }
    const previousTier = tierOrder[currentIndex - 1];
    const currentFeatures = SUBSCRIPTION_TIERS[tier].features;
    const previousFeatures = SUBSCRIPTION_TIERS[previousTier].features;
    return Object.keys(currentFeatures).filter((feature) => currentFeatures[feature] && !previousFeatures[feature]);
}
/**
 * Compare two tiers and return upgrade direction
 */
export function compareTiers(from, to) {
    const tierOrder = ['starter', 'growth', 'pro', 'scale'];
    const fromIndex = tierOrder.indexOf(from);
    const toIndex = tierOrder.indexOf(to);
    if (toIndex > fromIndex)
        return 'upgrade';
    if (toIndex < fromIndex)
        return 'downgrade';
    return 'same';
}
/**
 * Get the next tier up from the current tier
 * Returns null if already at highest tier
 */
export function getNextTier(current) {
    const tierOrder = ['starter', 'growth', 'pro', 'scale'];
    const currentIndex = tierOrder.indexOf(current);
    if (currentIndex === tierOrder.length - 1)
        return null;
    return tierOrder[currentIndex + 1] ?? null;
}
/**
 * Build a usage summary from raw usage data
 */
export function buildUsageSummary(tier, usage, additionalCounts = {}) {
    const config = getTierConfig(tier);
    const now = new Date();
    const periodStart = usage.periodStart ?? new Date(now.getFullYear(), now.getMonth(), 1);
    const periodEnd = usage.periodEnd ?? new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    const revenueCurrent = usage.trackedRevenueCents ?? 0;
    const aiCurrent = usage.aiContentGenerated ?? 0;
    const linksCurrent = usage.affiliateLinksCount ?? 0;
    const programsCurrent = usage.affiliateProgramsCount ?? 0;
    const teamCurrent = additionalCounts.teamMembersCount ?? 1;
    const revenuePercentage = getLimitUsagePercentage(tier, 'revenueLimitCents', revenueCurrent);
    const aiPercentage = getLimitUsagePercentage(tier, 'aiContentLimit', aiCurrent);
    // Determine if upgrade should be suggested
    let suggestedUpgrade = null;
    if (revenuePercentage >= 80 || aiPercentage >= 80) {
        suggestedUpgrade = getNextTier(tier);
    }
    return {
        tier,
        period: { start: periodStart, end: periodEnd },
        usage: {
            revenue: {
                current: revenueCurrent,
                limit: config.revenueLimitCents,
                percentage: revenuePercentage,
                isUnlimited: config.revenueLimitCents === -1,
            },
            aiContent: {
                current: aiCurrent,
                limit: config.aiContentLimit,
                percentage: aiPercentage,
                isUnlimited: config.aiContentLimit === -1,
            },
            affiliateLinks: {
                current: linksCurrent,
                limit: config.affiliateLinksLimit,
                percentage: getLimitUsagePercentage(tier, 'affiliateLinksLimit', linksCurrent),
                isUnlimited: config.affiliateLinksLimit === -1,
            },
            affiliatePrograms: {
                current: programsCurrent,
                limit: config.affiliateProgramsLimit,
                percentage: getLimitUsagePercentage(tier, 'affiliateProgramsLimit', programsCurrent),
                isUnlimited: config.affiliateProgramsLimit === -1,
            },
            teamMembers: {
                current: teamCurrent,
                limit: config.teamMembersLimit,
                percentage: getLimitUsagePercentage(tier, 'teamMembersLimit', teamCurrent),
                isUnlimited: config.teamMembersLimit === -1,
            },
        },
        warnings: {
            approachingRevenueLimit: revenuePercentage >= 80 && revenuePercentage < 100,
            revenueLimitReached: revenuePercentage >= 100,
            approachingAiLimit: aiPercentage >= 80 && aiPercentage < 100,
        },
        suggestedUpgrade,
    };
}
//# sourceMappingURL=subscriptions.js.map